import type { PropsWithChildren, ReactNode } from "react";
import React from "react";

type ConditionFn<T> = (value: T) => boolean;

interface CaseProps<T> {
  value: T;
  condition: T | ConditionFn<T>;
  children: ReactNode;
}

const Case = <T,>({ condition, value, children }: CaseProps<T>) => {
  if (typeof condition === "function") {
    return (condition as ConditionFn<T>)(value) ? children : null;
  }

  if (condition === value) {
    return children;
  }

  return null;
};

const Default: React.FC<PropsWithChildren> = ({ children }) => {
  return children;
};

function createCase<T>(value: T) {
  return (props: Omit<CaseProps<T>, "value">): JSX.Element | null => {
    return <Case {...props} value={value} />;
  };
}

export const MatchCase = <T,>({
  value,
  children,
}: {
  value: T;

  children: (
    CaseComp: React.ComponentType<Omit<CaseProps<T>, "value">>,
    Default: React.ComponentType<{
      children?: ReactNode | undefined;
    }>,
  ) => ReactNode;
}) => {
  return children(
    React.useMemo(() => createCase(value), [value]),
    Default,
  );
};
